import React, {
 useState, ReactNode, useCallback, useRef
} from 'react';
import { useTranslation, type TFunction } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../ui/dialog';
import { Button } from '../button';
import { Checkbox } from '../form/inputs/checkbox/checkbox-input';
import { TextInput } from '../form/inputs/text/text-input';
import { safelyOpenGlobalModal, setGlobalModal } from '../../global-manager';

type Props = {
  className?: string;
  children?: ReactNode;
  title: ReactNode;
  onConfirm: (checked: boolean) => any | Promise<any>;
  cancelText?: string;
  confirmText?: string;
  confirmString?: string;
  description?: ReactNode | (() => ReactNode);
  check?: string;
  onCancel?: Function;
  onEnter?: Function;
  onExit?: Function;
  danger?: boolean;
  isLoading?: boolean;
  showGlobalModal?: boolean;
  modal?: boolean;
};

type ConfirmModalProps = Omit<Props, 'children' | 'showGlobalModal'> & {
  onClose: () => void;
  t: TFunction;
  modal?: boolean;
};

function ConfirmModal({
  title,
  description,
  check,
  cancelText,
  confirmText,
  confirmString,
  onConfirm,
  onCancel,
  danger,
  onExit,
  isLoading,
  className,
  onClose,
  t,
  modal = true,
  isOpen = true,
}: ConfirmModalProps & { isOpen?: boolean }) {
  const [checked, setChecked] = useState(false);
  const [confirmValue, setConfirmValue] = useState('');
  const [isConfirming, setIsConfirming] = useState(false);
  const isMountedRef = useRef(true);

  React.useEffect(() => {
    isMountedRef.current = true;
    return () => { isMountedRef.current = false; };
  }, []);

  const handleConfirm = useCallback(async () => {
    const result = onConfirm(checked);
    const promise =
      result != null && typeof (result as Promise<unknown>).then === 'function'
        ? (result as Promise<void>)
        : null;
    if (promise) {
      setIsConfirming(true);
      try {
        await promise;
        if (isMountedRef.current) onClose();
      } catch {
        // Leave modal open on error
      } finally {
        if (isMountedRef.current) setIsConfirming(false);
      }
    } else {
      onClose();
    }
  }, [onConfirm, onClose, checked]);

  const handleCancel = () => {
    onCancel?.();
    onClose();
    onExit?.();
  };

  const modalClassName = className ? `ConfirmButton ${className}` : 'ConfirmButton';
  const sizeClass = !confirmString ? 'tw-w-[360px] tw-min-w-[360px] tw-max-w-[360px]'
  : 'tw-w-[500px] tw-min-w-[500px] tw-max-w-[500px]';

  const handleOpenChange = React.useCallback((open: boolean) => {
    if (!open) {
      onClose();
      onExit?.();
    }
  }, [onClose, onExit]);

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange} modal={modal}>
      <DialogContent
        className={`${sizeClass} ${modalClassName} tw-py-5 tw-px-10`}
        showCloseButton={false}
        onInteractOutside={(e) => {
          e.preventDefault();
        }}
        onEscapeKeyDown={(e) => {
          e.preventDefault();
        }}
      >
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {description ? (
            <DialogDescription className="ConfirmButton__description">
              {description}
            </DialogDescription>
          ) : (
            <DialogDescription className="tw-sr-only" />
          )}
        </DialogHeader>

        <div>
          {check && (
            <Checkbox
              label={check}
              value={checked}
              onChange={(value) => setChecked(!!value)}
            />
          )}

          {confirmString && (
            <TextInput
              value={confirmValue}
              onChange={(event) => setConfirmValue(event.target.value)}
            />
          )}
        </div>

        <DialogFooter className="tw-mt-6">
          <div className="tw-flex tw-gap-2 tw-justify-end tw-w-full">
            <Button
              type="transparent"
              size="large"
              onClick={handleCancel}
              className="tw-text-red-600 hover:tw-text-red-700"
              disabled={isConfirming}
            >
              {cancelText || t('common:confirm_button_cancel')}
            </Button>
            <Button
              size="large"
              type={danger ? 'warning' : 'primary'}
              onClick={handleConfirm}
              isLoading={isLoading || isConfirming}
              disabled={!!(confirmString && confirmString !== confirmValue) || isConfirming}
            >
              {confirmText || t('common:confirm_button_confirm_text')}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function resolveDescription(description?: ReactNode | (() => ReactNode)) {
  return typeof description === 'function' ? description() : description;
}

export const ConfirmButton = ({
  children,
  title,
  description,
  check,
  cancelText,
  confirmText,
  confirmString,
  onConfirm,
  onCancel,
  danger,
  onEnter,
  onExit,
  isLoading,
  className,
  showGlobalModal = true,
  modal = true,
}: Props) => {
  const { t } = useTranslation();
  const [isLocalOpen, setIsLocalOpen] = useState(false);

  const shouldUseGlobalModal = showGlobalModal
    && typeof document !== 'undefined'
    && !!document.getElementById('global-modal-root');

  const closeModal = useCallback(() => {
    if (shouldUseGlobalModal) {
      setGlobalModal(null);
    } else {
      setIsLocalOpen((prev) => {
        if (prev) return false;
        return prev;
      });
    }
  }, [shouldUseGlobalModal]);

  const resolvedDescription = resolveDescription(description);

  const modalElement = (
    <ConfirmModal
      title={title}
      description={resolvedDescription}
      check={check}
      cancelText={cancelText}
      confirmText={confirmText}
      confirmString={confirmString}
      onConfirm={onConfirm}
      onCancel={onCancel}
      danger={danger}
      onEnter={onEnter}
      onExit={onExit}
      isLoading={isLoading}
      className={className}
      onClose={closeModal}
      t={t}
      modal={modal}
      isOpen={!shouldUseGlobalModal ? (!children ? true : isLocalOpen) : true}
    />
  );

  const handleClick = useCallback((e: React.MouseEvent) => {
    // Stop propagation to prevent parent handlers from firing
    // This maintains React consistency by using event methods instead of DOM traversal
    e.stopPropagation();
    e.preventDefault();

    if (shouldUseGlobalModal) {
      safelyOpenGlobalModal(
        <ConfirmModal
          title={title}
          description={resolveDescription(description)}
          check={check}
          cancelText={cancelText}
          confirmText={confirmText}
          confirmString={confirmString}
          onConfirm={onConfirm}
          onCancel={onCancel}
          danger={danger}
          onEnter={onEnter}
          onExit={onExit}
          isLoading={isLoading}
          className={className}
          onClose={closeModal}
          t={t}
          modal={modal}
          isOpen
        />,
      );
    } else {
      setIsLocalOpen(true);
    }
  }, [
    shouldUseGlobalModal,
    title,
    description,
    check,
    cancelText,
    confirmText,
    confirmString,
    onConfirm,
    onCancel,
    danger,
    onEnter,
    onExit,
    isLoading,
    className,
    closeModal,
    t,
    modal,
  ]);

  const trigger =
    children &&
    React.cloneElement(React.Children.only(children) as React.ReactElement, {
      onClick: (e: any) => {
        const existingOnClick = (children as React.ReactElement)?.props?.onClick;
        if (existingOnClick) {
          existingOnClick(e);
        }
        handleClick(e);
      },
    });

  if (!children) {
    if (!shouldUseGlobalModal)
      return modalElement;
    safelyOpenGlobalModal(modalElement);
    return null;
  }

  const shouldRenderModal = !shouldUseGlobalModal && isLocalOpen;

  return (
    <>
      {trigger}
      {shouldRenderModal && modalElement}
    </>
  );
};
